import type { PropsWithChildren } from 'react';
import React from 'react';
import type {
  Candidate,
  CandidateOpportunity,
  Opportunity,
  OpportunityCandidate,
  Taxonomy,
  Position,
} from '@axiom/validation';
import { Flashy } from '@axiom/ui';
import {
  CandidatesConst,
  CountryCodesConst,
  IndustryValue,
  OpportunitiesConst,
  PositionsConst,
  StateCodesConst,
  TaxonomyConst,
} from '@axiom/const';

import { CandidateUtil } from '../../utils/candidate-util';
import { PositionUtil } from '../../utils/position-util';

const { CountryCodesAbbreviations } = CountryCodesConst;
const { WorkLocation } = OpportunitiesConst;
const { AbbreviatedStateCode, StateCodeMap } = StateCodesConst;

export type MatchChecklistItem = PropsWithChildren<{
  id: string;
  isMatched?: boolean;
}>;

const isLawyerBusinessTitle = (position: Position | null) => {
  return position?.businessTitle === TaxonomyConst.LawyerBusinessTitle;
};

export const OpportunityTalentMatchSummaryChecklistUtil = {
  getCountryChecklistItem({
    candidate,
    opportunity,
  }: {
    candidate: Candidate;
    opportunity: Opportunity;
  }): MatchChecklistItem | undefined {
    if (
      !candidate.addressCountry ||
      !candidate.addressCountryCode ||
      !opportunity.requiredTalentCountryCode
    ) {
      return undefined;
    }

    return {
      id: 'checklist-item-country',
      isMatched:
        candidate.addressCountryCode === opportunity.requiredTalentCountryCode,
      children: (
        <>
          Located in <Flashy bold>{candidate.addressCountry}</Flashy>
        </>
      ),
    };
  },
  getStateChecklistItem({
    candidate,
    opportunity,
  }: {
    candidate: Candidate;
    opportunity: Opportunity;
  }): MatchChecklistItem | undefined {
    if (
      opportunity.requiredTalentCountryCode === 'US' &&
      opportunity.addressState &&
      candidate.addressCountryCode === 'US'
    ) {
      const id = 'checklist-item-state';
      const isMatched =
        opportunity.addressState ===
          AbbreviatedStateCode(candidate.addressState) ||
        opportunity.addressState === candidate.addressState;

      if (!candidate.addressState) {
        return {
          id,
          isMatched,
          children: 'Unknown home state',
        };
      }

      return {
        id,
        isMatched,
        children: (
          <>
            Located in <Flashy bold>{candidate.addressState}</Flashy>
          </>
        ),
      };
    }

    return undefined;
  },
  getIndustryChecklistItem({
    candidate,
    opportunity,
    industries,
  }: {
    candidate: Candidate;
    opportunity: Opportunity;
    industries: Taxonomy['industries'];
  }): MatchChecklistItem | undefined {
    if (
      !opportunity.account?.industryValue ||
      opportunity.account.industryValue === IndustryValue.NA
    ) {
      return undefined;
    }

    const candidateIndustry = candidate.industries?.find(
      i => i.industryValue === opportunity.account?.industryValue
    );

    const yearsOfExperience = candidateIndustry?.yearsOfExperience || '0';
    const yearString = CandidateUtil.formatYOE(yearsOfExperience);
    const isMatched = Number(yearsOfExperience) > 0;

    return {
      id: 'checklist-item-industry',
      isMatched,
      children: (
        <>
          {isMatched ? yearString : 'No'} experience in{' '}
          <Flashy bold>
            {industries[opportunity.account.industryValue]?.displayValue}
          </Flashy>
        </>
      ),
    };
  },
  getPracticeAreaChecklistItem({
    candidate,
    opportunity,
    practiceAreas,
  }: {
    candidate: Candidate;
    opportunity: Opportunity;
    practiceAreas: Taxonomy['practiceAreas'];
  }): MatchChecklistItem | undefined {
    if (!opportunity.primaryPracticeArea || !candidate.primaryPracticeArea) {
      return undefined;
    }

    return {
      id: 'checklist-item-practice-area',
      isMatched:
        opportunity.primaryPracticeArea === candidate.primaryPracticeArea,
      children: (
        <>
          Practice Area is{' '}
          <Flashy bold>
            {practiceAreas[candidate.primaryPracticeArea]?.fullName}
          </Flashy>
        </>
      ),
    };
  },
  getBarredToWorkOnsiteChecklistItem({
    candidate,
    opportunity,
    opportunityCandidate,
  }: {
    candidate: Candidate;
    opportunity: Opportunity;
    opportunityCandidate?: OpportunityCandidate;
  }): MatchChecklistItem | undefined {
    if (
      opportunity.country === CountryCodesAbbreviations.US &&
      (opportunityCandidate?.position
        ? isLawyerBusinessTitle(opportunityCandidate.position)
        : opportunity.positions.some(position =>
            isLawyerBusinessTitle(position)
          ))
    ) {
      const id = 'checklist-item-barred-to-work-onsite';
      // onsite, hybrid or no preference
      if (
        opportunity.workLocation !== WorkLocation.Remote &&
        opportunity.addressState
      ) {
        const state =
          StateCodeMap.get(opportunity.addressState) ??
          opportunity.addressState;
        // is candidate admitted to work in the opportunity state
        if (CandidateUtil.isBarredInState(candidate.barredLocations, state)) {
          return {
            id,
            isMatched: true,
            children: (
              <>
                Admitted to work in <Flashy bold>{state}</Flashy>
              </>
            ),
          };
        }
        return {
          id,
          isMatched: false,
          children: (
            <>
              Not admitted to work in{' '}
              <Flashy bold>
                {state}
                {CandidateUtil.isGreenState(state)
                  ? ' (green state)'
                  : ' (red state)'}
              </Flashy>
            </>
          ),
        };
      }
    }
    return undefined;
  },
  getBarredToWorkRemotelyChecklistItem({
    candidate,
    opportunity,
    opportunityCandidate,
  }: {
    candidate: Candidate;
    opportunity: Opportunity;
    opportunityCandidate?: OpportunityCandidate;
  }): MatchChecklistItem | undefined {
    if (
      opportunity.country === CountryCodesAbbreviations.US &&
      candidate.addressCountryCode === CountryCodesAbbreviations.US &&
      (opportunityCandidate?.position
        ? isLawyerBusinessTitle(opportunityCandidate.position)
        : opportunity.positions.some(position =>
            isLawyerBusinessTitle(position)
          ))
    ) {
      const id = 'checklist-item-barred-to-work-remotely';
      // remote, hybrid or no preference
      if (
        opportunity.workLocation !== WorkLocation.Onsite &&
        candidate.addressState
      ) {
        // candidate is admitted to work in the state they live
        if (
          CandidateUtil.isBarredInState(
            candidate.barredLocations,
            candidate.addressState
          )
        ) {
          return {
            id,
            isMatched: true,
            children: (
              <>
                Admitted to work remotely in{' '}
                <Flashy bold>{candidate.addressState}</Flashy>
              </>
            ),
          };
        }
        return {
          id,
          isMatched: false,
          children: (
            <>
              Not admitted to work remotely in{' '}
              <Flashy bold>
                {candidate.addressState}
                {CandidateUtil.isGreenState(candidate.addressState)
                  ? ' (green state)'
                  : ' (red state)'}
              </Flashy>
            </>
          ),
        };
      }
    }
    return undefined;
  },
  getCrossFulfillmentChecklistItem({
    candidate,
    candidateOpportunities,
  }: {
    candidate: Candidate;
    candidateOpportunities: CandidateOpportunity[];
    opportunity: Opportunity;
  }): MatchChecklistItem | undefined {
    const counts = CandidateUtil.getCrossFulfillmentCounts(
      candidateOpportunities
    );

    if (counts.total > 0) {
      const crossFulfillMessages =
        CandidateUtil.formatCrossFulfillmentCountsMessage(counts);

      return {
        id: 'checklist-item-cross',
        isMatched: !candidate.isCrossFulfilled,
        children: (
          <>
            <Flashy bold>Cross:</Flashy> {crossFulfillMessages.join(', ')}
          </>
        ),
      };
    }

    return undefined;
  },
  getAvailabilityHoursChecklistItem({
    candidate,
    opportunity,
    opportunityCandidate,
  }: {
    candidate: Candidate;
    opportunity: Opportunity;
    opportunityCandidate?: OpportunityCandidate;
  }): MatchChecklistItem | undefined {
    if (
      opportunityCandidate?.position?.billingHoursPerWeek ||
      opportunity.positions.length === 1
    ) {
      const weeklyAvailability = candidate.weeklyAvailability ?? 0;
      const positionHours =
        opportunityCandidate?.position?.billingHoursPerWeek ||
        opportunity.positions[0]?.billingHoursPerWeek ||
        0;
      const isMatched = weeklyAvailability >= positionHours;

      return {
        id: 'checklist-item-hours-availability',
        isMatched,
        children: (
          <>
            {!isMatched && weeklyAvailability > 0 ? 'Only ' : undefined}
            <Flashy bold>
              {Math.round(weeklyAvailability)}{' '}
              {weeklyAvailability === 1 ? 'hour' : 'hours'}
            </Flashy>{' '}
            available
          </>
        ),
      };
    }

    return undefined;
  },
  getEngagedHoursChecklistItem({
    candidate,
    opportunity,
    candidateOpportunities,
  }: {
    candidate: Candidate;
    opportunity: Opportunity;
    candidateOpportunities: CandidateOpportunity[];
  }): MatchChecklistItem | undefined {
    let engagedHrsWithPosition =
      candidate.calculatedEngagedAndReservedHours?.totalEngagedHours ?? 0;
    let reservedHrsWithPosition =
      candidate.calculatedEngagedAndReservedHours?.totalReservedHours ?? 0;
    const selectedCandidatePosition = candidateOpportunities.find(
      opp => opp.positionId && opp.opportunityId === opportunity.id
    )?.position;

    const positions = PositionUtil.sortPositions(opportunity.positions);

    // If candidate On Position
    if (selectedCandidatePosition) {
      if (
        selectedCandidatePosition.reservedType ===
        PositionsConst.ReservedType.Reserved
      ) {
        reservedHrsWithPosition += PositionUtil.calculatePositionHoursPerWeek(
          selectedCandidatePosition
        );
        engagedHrsWithPosition += PositionUtil.calculatePositionHoursPerWeek(
          selectedCandidatePosition
        );
      }

      if (
        selectedCandidatePosition.reservedType === null ||
        selectedCandidatePosition.reservedType ===
          PositionsConst.ReservedType.NotReserved
      ) {
        engagedHrsWithPosition += PositionUtil.calculatePositionHoursPerWeek(
          selectedCandidatePosition
        );
      }
      // If candidate on Talent Pool
    } else {
      if (positions[0]?.reservedType === PositionsConst.ReservedType.Reserved) {
        reservedHrsWithPosition += PositionUtil.calculatePositionHoursPerWeek(
          positions[0]
        );
        engagedHrsWithPosition += PositionUtil.calculatePositionHoursPerWeek(
          positions[0]
        );
      }

      if (
        positions[0]?.reservedType === null ||
        positions[0]?.reservedType === PositionsConst.ReservedType.NotReserved
      ) {
        engagedHrsWithPosition += PositionUtil.calculatePositionHoursPerWeek(
          positions[0]
        );
      }
    }
    if (
      engagedHrsWithPosition >= CandidatesConst.EngagedHoursWarningLimit ||
      reservedHrsWithPosition >= CandidatesConst.ReservedHoursWarningLimit
    ) {
      return {
        id: 'checklist-item-engaged-hours',
        isMatched: false,
        children: (
          <>
            Talent already engaged for{' '}
            <Flashy bold>
              {candidate.calculatedEngagedAndReservedHours?.totalEngagedHours ??
                0}{' '}
              hours (
              {candidate.calculatedEngagedAndReservedHours
                ?.totalReservedHours ?? 0}{' '}
              reserved)
            </Flashy>{' '}
          </>
        ),
      };
    }
    return undefined;
  },
  getOpenToLowerEscItem({
    opportunityCandidate,
  }: {
    opportunityCandidate?: OpportunityCandidate;
  }): MatchChecklistItem | undefined {
    if (!opportunityCandidate?.openToLowerEsc) {
      return undefined;
    }

    return {
      id: 'checklist-item-open-to-lower-esc',
      isMatched: true,
      children: (
        <>
          OK with{' '}
          <Flashy bold>
            lower ESC
            {opportunityCandidate.openToLowerEscCompensationPercentage
              ? ` (${opportunityCandidate.openToLowerEscCompensationPercentage}%)`
              : ''}
          </Flashy>
        </>
      ),
    };
  },
};
